import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import PackageAssignModal from "./ipd-billing/components/PackageAssignModal";
import {
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
  getChargeCapture,
  cancelCharge,
  updateCharge,
} from "./ipd-billing/billing-api";
import {
  listEmergencyBillingVisits,
  emergencyLedgerToChargeItems,
  emergencyVisitToPatientContext,
  chargeRowActions,
  inCategory,
} from "./ipd-billing/emergency-billing-source";
import {
  isEmergencySubject,
  subjectIdOf,
} from "./ipd-billing/billing-subject";
import {
  listEmergencyCharges,
  postPendingCharges,
  reverseEmergencyCharges,
} from "./emergency/api/emergencyBillingApi";
import { ensureBillingSetup, listBillingAccountPackages, addServiceToPackage } from "./ipd-billing/package-billing-api";
import useEdCarryOver from "./ipd-billing/useEdCarryOver";
import IpdButton from "./ipd/utils/IpdButton";

// Category pills — seeded so the filter row renders before the first fetch;
// replaced by `category_filters` from GET .../charge-capture/ once loaded.
const DEFAULT_CATEGORY_FILTERS = [
  { id: "ALL",          label: "All" },
  { id: "CONSULTATION", label: "Consultation" },
  { id: "BED",          label: "Bed" },
  { id: "NURSING",      label: "Nursing" },
  { id: "PROCEDURE",    label: "Procedure" },
  { id: "OT",           label: "OT" },
  { id: "PHARMACY",     label: "Pharmacy" },
  { id: "LAB",          label: "Lab" },
  { id: "RADIOLOGY",    label: "Radiology" },
  { id: "CONSUMABLES",  label: "Consumables" },
  { id: "PACKAGES",     label: "Packages" },
  { id: "MISC",         label: "Misc" },
];

// ── LOOKUP MAPS ───────────────────────────────────────────────────────────────
const SERVICE_GROUP_COLORS = {
  "Room & Bed":   { bg: "#E9F5FE", color: "#0d233f" },
  Nursing:        { bg: "#f3e8ff", color: "#7c3aed" },
  Laboratory:     { bg: "#ccfbf1", color: "#0f766e" },
  Radiology:      { bg: "#e0e7ff", color: "#4338ca" },
  Pharmacy:       { bg: "#fff8e1", color: "#856404" },
  Procedures:     { bg: "#ffedd5", color: "#c2410c" },
  Consultation:   { bg: "#d1f2db", color: "#145a32" },
  Consumables:    { bg: "#f1f5f9", color: "#475569" },
  OT:             { bg: "#fdecea", color: "#842029" },
  default:        { bg: "#f1f5f9", color: "#475569" },
};

// ── PACKAGE STATUS ──────────────────────────────────────────────────────────
// One clean badge per package-pricing outcome, covering every status the billing
// engine emits (INCLUDED / PARTIALLY_INCLUDED / LIMIT_EXCEEDED / EXCLUDED /
// OUT_OF_PACKAGE). Anything else falls through to a neutral title-cased label.
const PACKAGE_PRICING_BADGE = {
  INCLUDED:           { label: "Included",       bg: "#d1f2db", color: "#145a32" },
  PARTIALLY_INCLUDED: { label: "Partial",        bg: "#fff3cd", color: "#856404" },
  LIMIT_EXCEEDED:     { label: "Over limit",     bg: "#ffe5d0", color: "#9a4a00" },
  EXCLUDED:           { label: "Excluded",       bg: "#fdecea", color: "#842029" },
  OUT_OF_PACKAGE:     { label: "Out of package", bg: "#eef1f4", color: "#5a6572" },
};

function packagePricingBadge(status) {
  if (!status) return null;
  const key = String(status).toUpperCase();
  return (
    PACKAGE_PRICING_BADGE[key] || {
      label: key.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()),
      bg: "#eef1f4",
      color: "#5a6572",
    }
  );
}

// Bed day charges are auto-posted one row per day (each qty 1). For display only, collapse a
// bed's day-rows (service code BED-*) into ONE line: quantity = number of days, amount = the sum.
// The underlying per-day charges are untouched (packages consume day-limits and invoicing/reversal
// still work per day). Non-bed rows pass through unchanged. Order is preserved (aggregate sits at
// the first bed row's position).
function aggregateBedCharges(rows) {
  const isBed = (r) => String(r.code || "").toUpperCase().startsWith("BED-");
  const out = [];
  const groups = new Map(); // bed service code -> aggregated row (also present in `out`)
  for (const r of rows) {
    if (!isBed(r)) { out.push(r); continue; }
    const key = r.code;
    const agg = groups.get(key);
    if (!agg) {
      const first = { ...r, _bedDays: 1, _eventIds: [r.charge_event_id || r.id], _statuses: new Set([r.packagePricingStatus || "NONE"]) };
      groups.set(key, first);
      out.push(first);
    } else {
      agg.qty = Number(agg.qty || 0) + Number(r.qty || 0);
      agg.amount = Number(agg.amount || 0) + Number(r.amount || 0);
      agg._bedDays += 1;
      agg._eventIds.push(r.charge_event_id || r.id);
      agg._statuses.add(r.packagePricingStatus || "NONE");
    }
  }
  // Finalise: only rows with >1 day become an "aggregated" line; collapse the combined package status.
  for (const agg of groups.values()) {
    if (agg._bedDays > 1) {
      agg._aggregated = true;
      const s = agg._statuses;
      const hasIncl = s.has("INCLUDED") || s.has("PARTIALLY_INCLUDED");
      const hasOut = s.has("OUT_OF_PACKAGE") || s.has("NONE");
      agg.packagePricingStatus = (hasIncl && hasOut) ? "PARTIALLY_INCLUDED" : (hasIncl ? "INCLUDED" : agg.packagePricingStatus);
    }
    delete agg._statuses;
  }
  return out;
}

// Package chip in the Charge Capture sub-header — used for the single-package
// chip and for the "N assigned packages" summary that opens the picker.
const PKG_CHIP_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "2px 9px",
  borderRadius: "100px",
  background: "#d1f2db",
  color: "#145a32",
  fontSize: "11px",
  fontWeight: "700",
  whiteSpace: "nowrap",
  border: "none",
  cursor: "pointer",
};

// ── SKELETON ──────────────────────────────────────────────────────────────────
const shimmerStyle = {
  background: "linear-gradient(90deg,#e9ecef 25%,#f8f9fa 50%,#e9ecef 75%)",
  backgroundSize: "200% 100%",
  animation: "ipdShimmer 1.4s infinite",
  borderRadius: "4px",
};

// ── DELETE CHARGE MODAL ─────────────────────────────────────────────────────────
function DeleteChargeModal({ charge, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const presets = [
    "Entered in error",
    "Duplicate charge",
    "Service not rendered",
    "Incorrect amount / quantity",
    "Billed to wrong patient",
  ];

  const canDelete = reason.trim().length > 0 && !submitting;

  const handleConfirm = async () => {
    if (!canDelete) return;
    setSubmitting(true);
    setError("");
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err.message || "Failed to cancel charge");
      setSubmitting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: "14px", padding: "28px", width: "440px", boxShadow: "0 20px 60px rgba(13,35,63,0.25)" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: C.text }}>Delete Charge</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px" }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div style={{ background: "#fafbfc", border: `1px solid ${C.border}`, borderRadius: "9px", padding: "12px", marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: C.text, marginBottom: "3px" }}>{charge.description}</div>
          <div style={{ fontSize: "12px", color: C.textSub }}>
            {charge.medicalService || "—"} · Qty {charge.qty} · {fmtINR(charge.amount)}
          </div>
        </div>

        <div>
          <label style={labelStyle}>Reason for deletion <span style={{ color: C.danger }}>*</span></label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
            {presets.map(p => {
              const active = reason === p;
              return (
                <button key={p} type="button" onClick={() => setReason(p)}
                  style={{ padding: "5px 11px", borderRadius: "100px", border: `1.5px solid ${active ? C.primary : C.border}`, background: active ? C.primary : "#fff", color: active ? "#fff" : C.textSub, fontSize: "12px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s" }}>
                  {p}
                </button>
              );
            })}
          </div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Enter or refine the reason…" rows={3}
            style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
        </div>

        {error && (
          <div style={{ marginTop: "14px", background: "#fdecea", border: "1px solid #f5c2c7", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#842029", fontWeight: "600" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={submitting} sx={{ flex: 1 }}>
            Cancel
          </IpdButton>
          <IpdButton tone="red" onClick={handleConfirm} disabled={!canDelete} loading={submitting} sx={{ flex: 2 }}>
            Delete Charge
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── EDIT CHARGE MODAL ───────────────────────────────────────────────────────────
function EditChargeModal({ charge, onClose, onConfirm }) {
  const qtyLocked = charge.quantityModifiable === false;
  const [qty, setQty]             = useState(String(charge.qty ?? 1));
  const [description, setDescription] = useState(charge.description || "");
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState("");

  const qtyNum   = Number(qty);
  const dirty    = qtyNum !== Number(charge.qty) || description.trim() !== (charge.description || "");
  const canSave  = !submitting && dirty && Number.isInteger(qtyNum) && qtyNum >= 1 && description.trim().length > 0;
  const newAmount = Number(charge.rate || 0) * (qtyNum || 0);

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setError("");
    try {
      await onConfirm({ quantity: qtyNum, remarks: description.trim() });
    } catch (err) {
      setError(err.message || "Failed to update charge");
      setSubmitting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: "14px", padding: "28px", width: "460px", boxShadow: "0 20px 60px rgba(13,35,63,0.25)" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: C.text }}>Edit Charge</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px" }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div style={{ background: "#fafbfc", border: `1px solid ${C.border}`, borderRadius: "9px", padding: "12px", marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: C.text, marginBottom: "3px" }}>{charge.medicalService || charge.description}</div>
          <div style={{ fontSize: "12px", color: C.textSub }}>
            {charge.serviceGroup || "—"} · {charge.code || "—"}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={labelStyle}>Description *</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Charge description" style={inputStyle} />
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Qty</label>
              {qtyLocked ? (
                <input type="number" value={qty} readOnly tabIndex={-1}
                  style={{ ...inputStyle, background: "#f1f5f9", color: C.textSub, cursor: "not-allowed" }} />
              ) : (
                <input type="number" min="1" step="1" value={qty}
                  onKeyDown={e => { if ([".", ",", "e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                  onChange={e => setQty(e.target.value.replace(/\D/g, ""))} style={inputStyle} />
              )}
            </div>
            <div style={{ flex: 1.3 }}>
              <label style={labelStyle}>Rate (₹)</label>
              <input type="number" value={charge.rate} readOnly tabIndex={-1}
                style={{ ...inputStyle, background: "#f1f5f9", color: C.textSub, cursor: "not-allowed" }} />
            </div>
            <div style={{ flex: 1.3 }}>
              <div style={{ padding: "9px 12px", background: C.accent, border: "1px solid #b8d4ee", borderRadius: "7px", fontSize: "13px", fontWeight: "700", color: C.text, textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtINR(newAmount)}
              </div>
            </div>
          </div>
          {qtyLocked && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "600", color: C.textSub }}>
              <SvgIcon name="lock" size={11} color={C.textSub} /> Qty locked by service master
            </span>
          )}
        </div>

        {error && (
          <div style={{ marginTop: "14px", background: "#fdecea", border: "1px solid #f5c2c7", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#842029", fontWeight: "600" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={submitting} sx={{ flex: 1 }}>
            Cancel
          </IpdButton>
          <IpdButton onClick={handleSave} disabled={!canSave} loading={submitting} sx={{ flex: 2 }}>
            Save Changes
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── PAGE ──────────────────────────────────────────────────────────────────────
export default function IPDBillingChargeCapture() {
  const location      = useLocation();
  const history        = useHistory();
  const passedPatient = location.state?.patient || null;

  // ── Patient selection (same dropdown as Invoice & Settlement) ──────────────
  const [selectedPatient,   setSelectedPatient]   = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions,        setAdmissions]        = useState([]);
  const [searchQuery,       setSearchQuery]       = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError,       setSearchError]       = useState("");

  const encounterId = selectedPatient?.encounter_id || resolveAdmissionId(selectedPatient) || "";
  const patientName = selectedPatient?.name || "—";
  const wardBed     = `${selectedPatient?.ward || "—"} / ${selectedPatient?.bed || "—"}`;

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      const [ipdResult, erResult] = await Promise.allSettled([
        listAllocatedAdmissions("Allocated"),
        listEmergencyBillingVisits(),
      ]);
      const ipd = ipdResult.status === "fulfilled" ? ipdResult.value : [];
      const er = erResult.status === "fulfilled" ? erResult.value : [];
      setAdmissions([...ipd, ...er]);
    } catch (err) {
      console.error("Admissions fetch error:", err);
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => {
    loadAdmissions();
  }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const handleSelectAdmission = useCallback((row) => {
    const patient = isEmergencySubject(row)
      ? emergencyVisitToPatientContext(row)
      : admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setSearchQuery("");
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    clearAdmissionContext();
    setSearchQuery("");
    setCharges([]);
  }, []);

  // Charge-capture screen data (GET .../billing/charge-capture/).
  const [charges,          setCharges]          = useState([]);
  const [categoryFilters,  setCategoryFilters]  = useState(DEFAULT_CATEGORY_FILTERS);
  const [activeCategory,   setActiveCategory]   = useState("ALL");
  // Emergency visits only: may this user reverse a charge (emergency_charge_adjust).
  const [edCanAdjust,      setEdCanAdjust]      = useState(false);
  // Server-side date filter — default (false) shows only today's charges
  // (today=true on the API); toggling this shows backdated charges too
  // (today=false). Purely a display filter — charges are saved regardless.
  const [showAllDates,     setShowAllDates]     = useState(false);
  const [totals,           setTotals]           = useState({ todaysTotal: 0, autoCaptured: 0, manualEntries: 0, pendingApproval: 0 });
  const [defaultChargeDate, setDefaultChargeDate] = useState(null);
  const [sortConfig,       setSortConfig]       = useState({ key: null, direction: "asc" });
  const [chargeToDelete,   setChargeToDelete]   = useState(null);
  const [chargeToEdit,     setChargeToEdit]     = useState(null);
  const [isLoading,        setIsLoading]        = useState(false);
  const [loadError,        setLoadError]        = useState("");
  const [syncAgo,          setSyncAgo]          = useState(0);

  // Server-side filtering: refetch whenever the admission or category changes.
  const loadCharges = useCallback(async () => {
    if (!encounterId && !isEmergencySubject(selectedPatient)) {
      setCharges([]);
      setTotals({ todaysTotal: 0, autoCaptured: 0, manualEntries: 0, pendingApproval: 0 });
      return;
    }
    setIsLoading(true);
    setLoadError("");
    try {
      if (isEmergencySubject(selectedPatient)) {
        const visitId = subjectIdOf(selectedPatient);
        // Anything the ED recorded but has not posted yet goes to billing first,
        // so the clerk sees the whole visit. Its failures stay in the ED charge check.
        await postPendingCharges(visitId).catch(() => {});
        const { charges: ledgerCharges, canAdjust } = await listEmergencyCharges(visitId);
        const mappedItems = emergencyLedgerToChargeItems(ledgerCharges);
        setCharges(inCategory(mappedItems, activeCategory));
        setEdCanAdjust(canAdjust);
        const todaysTotal = mappedItems
          .filter((it) => it.status !== "REVERSED")
          .reduce((sum, it) => sum + Number(it.amount || 0), 0);
        // The card shows amounts, as the IPD totals are.
        setTotals({
          todaysTotal,
          autoCaptured: 0,
          manualEntries: todaysTotal,
          pendingApproval: 0,
        });
        setSyncAgo(0);
        return;
      }
      const res = await getChargeCapture(encounterId, { today: !showAllDates, category: activeCategory });
      // IGNORED rows are voided/superseded charge events — the API still
      // returns them, but they must not appear on the capture grid.
      setCharges(res.items.filter(it => String(it.status).toUpperCase() !== "IGNORED"));
      setTotals(res.totals);
      if (res.categoryFilters.length) setCategoryFilters(res.categoryFilters);
      if (res.defaultChargeDate) setDefaultChargeDate(res.defaultChargeDate);
      setSyncAgo(0);
    } catch (err) {
      console.error("Charge Capture fetch error:", err);
      setLoadError(err.message || "Failed to load charges");
      setCharges([]);
    } finally {
      setIsLoading(false);
    }
  }, [encounterId, selectedPatient, activeCategory, showAllDates]);

  useEffect(() => {
    loadCharges();
  }, [loadCharges]);

  // An admission picks up the ED charges of the visit it came from.
  const carryOverError = useEdCarryOver(selectedPatient, loadCharges);

  // Package status (Modules 3-4) — billing_setup is idempotent, so calling it
  // here (not just at bed allocation) safely gets billing_account_id for any
  // admission opened on this screen; the quick-header 400 for "no assignment
  // yet" is treated as null by getPackageQuickHeader, not an error.
  const [billingSetup,     setBillingSetup]     = useState(null);
  // Multiple packages can be assigned to one admission; hold them all (priority order).
  const [packages,         setPackages]         = useState([]);
  const [packageLoading,   setPackageLoading]   = useState(false);
  const [showAssignModal,  setShowAssignModal]  = useState(false);
  // Open state of the "N assigned packages" picker (only rendered when > 1).
  const [pkgMenuOpen,      setPkgMenuOpen]      = useState(false);
  // Row (out-of-package charge) queued for the "Include in package" modal.
  const [includeRow,       setIncludeRow]       = useState(null);

  const loadPackageStatus = useCallback(async (admissionId) => {
    if (!admissionId || isEmergencySubject(selectedPatient)) {
      setBillingSetup(null);
      setPackages([]);
      return;
    }
    setPackageLoading(true);
    try {
      const setup = await ensureBillingSetup(admissionId);
      setBillingSetup(setup);
      const list = setup.billingAccountId ? await listBillingAccountPackages(setup.billingAccountId) : [];
      setPackages(Array.isArray(list) ? list.filter((p) => p.status === "ACTIVE") : []);
    } catch (err) {
      console.error("Package status fetch error:", err);
      setBillingSetup(null);
      setPackages([]);
    } finally {
      setPackageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackageStatus(encounterId);
  }, [encounterId, loadPackageStatus]);

  useEffect(() => {
    const interval = setInterval(() => setSyncAgo(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  // Category filtering is server-side; only sorting is applied client-side.
  const sortedCharges = sortConfig.key
    ? [...charges].sort((a, b) => {
        const av = String(a[sortConfig.key] ?? "").toLowerCase();
        const bv = String(b[sortConfig.key] ?? "").toLowerCase();
        if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
        if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      })
    // Default view: group by service group, then chronological by charge date.
    : [...charges].sort((a, b) => {
        const ga = String(a.serviceGroup ?? "").toLowerCase();
        const gb = String(b.serviceGroup ?? "").toLowerCase();
        if (ga !== gb) return ga < gb ? -1 : 1;
        const da = String(a.charge_date || a.time || "");
        const db = String(b.charge_date || b.time || "");
        return da < db ? -1 : da > db ? 1 : 0;
      });

  // Display only: collapse each bed's per-day rows into one line (qty = days, amount = sum).
  const displayCharges = aggregateBedCharges(sortedCharges);

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const { todaysTotal, autoCaptured, manualEntries, pendingApproval } = totals;

  // Corporate contract signals (present only when charges carry a rate-card snapshot).
  const hasCorporate = charges.some((c) => c.pricingApplied);
  const corporateSavings = charges.reduce((s, c) => s + (c.corporateDiscountAmount || 0), 0);

  const syncLabel = syncAgo < 60
    ? `Auto-sync · ${syncAgo}s ago`
    : `Auto-sync · ${Math.floor(syncAgo / 60)}m ago`;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const openPackageDashboard = (pkg) => {
    setPkgMenuOpen(false);
    history.push("/ipd-billing/package-dashboard", {
      admissionId: encounterId,
      assignmentId: pkg.id,
      billingAccountId: billingSetup?.billingAccountId,
      patientName,
    });
  };

  // Cancel a charge with a reason (POST .../charges/{eventId}/cancel/).
  // Errors propagate so the modal can surface them; success refreshes the list.
  const handleDeleteCharge = async (charge, reason) => {
    if (isEmergencySubject(selectedPatient)) {
      const visitId = subjectIdOf(selectedPatient);
      await reverseEmergencyCharges(visitId, [charge.charge_event_id || charge.id], reason);
      setChargeToDelete(null);
      await loadCharges();
      return;
    }
    await cancelCharge(encounterId, charge.charge_event_id || charge.id, reason);
    setChargeToDelete(null);
    await loadCharges();
  };

  // Update a charge's qty/description (POST .../charges/{eventId}/update/).
  // Errors propagate so the modal can surface them; success refreshes the list.
  const handleUpdateCharge = async (charge, payload) => {
    await updateCharge(encounterId, charge.charge_event_id || charge.id, payload);
    setChargeToEdit(null);
    await loadCharges();
  };

  // Include an out-of-package charge into the patient's package (per-patient add-on).
  // Adds a covered entitlement for this service on the chosen package, then re-evaluates
  // THIS charge so it flips from out-of-package to included. Errors propagate to the modal.
  const handleIncludeInPackage = async (row, { assignmentId, limitType, amountLimit, reason }) => {
    await addServiceToPackage(encounterId, assignmentId, {
      serviceCode: row.code,
      serviceName: row.medicalService,
      limitType: limitType || "NO_LIMIT",
      amountLimit,
      reason,
      // An aggregated bed line stands for many day charges — re-evaluate them all.
      chargeEventIds: row._eventIds && row._eventIds.length ? row._eventIds : [row.charge_event_id || row.id],
    });
    setIncludeRow(null);
    await loadCharges();
  };

  // Charges persist immediately via the Add Charge modal (bulk-post writes the
  // ledger debit). This button re-syncs today's charges/totals from the account.
  const [isPosting,   setIsPosting]   = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const handlePostCharges = async () => {
    setIsPosting(true);
    try {
      await loadCharges();
      setPostSuccess(true);
      setTimeout(() => setPostSuccess(false), 2500);
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <IPDBillingLayout activeNav="charge">
      <style>{`
        .charge-row:hover { background: #f8f9fa !important; }
        .filter-tab:hover { background: #E9F5FE !important; color: #0d233f !important; }
      `}</style>

      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px", minHeight: 0 }}>

        {/* ── PATIENT SELECTION ── */}
        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to capture charges"
        />

        {carryOverError && (
          <div role="alert" style={{ background: "#FFF8E1", border: `1px solid ${C.warning}`, borderRadius: "12px", padding: "12px 16px", fontSize: "13px", color: C.text }}>
            {carryOverError}
          </div>
        )}

        {!selectedPatient ? (
          <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)", padding: "64px 32px", textAlign: "center" }}>
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "8px" }}>No patient selected</div>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub, lineHeight: 1.6 }}>
              Search and select an allocated patient above to capture their charges.
            </p>
          </div>
        ) : (
        <div style={{ display: "flex", gap: "20px", minHeight: 0 }}>

        {/* ── LEFT: CHARGE LIST ── */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Page title + actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Charge Capture</h2>
              <p style={{ margin: 0, fontSize: "13px", color: C.textSub, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span>Encounter {encounterId} · {patientName} · {wardBed}</span>
                {hasCorporate && (
                  <span style={{ padding: "2px 9px", borderRadius: "100px", background: "#d1f2db", color: "#145a32", fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap" }}>
                    Corporate contract pricing
                  </span>
                )}
                {/* One package → chip straight to its dashboard. Several → a count
                    chip that opens a picker; picking one opens the same dashboard. */}
                {packages.length === 1 && (
                  <button
                    onClick={() => openPackageDashboard(packages[0])}
                    title="View package dashboard"
                    style={PKG_CHIP_STYLE}
                  >
                    <SvgIcon name="package" size={11} color="#145a32" />
                    Package: {packages[0].packageCode} · {packages[0].status}
                  </button>
                )}
                {packages.length > 1 && (
                  <span style={{ position: "relative", display: "inline-flex" }}>
                    <button
                      onClick={() => setPkgMenuOpen((o) => !o)}
                      title="View assigned packages"
                      style={PKG_CHIP_STYLE}
                    >
                      <SvgIcon name="package" size={11} color="#145a32" />
                      {packages.length} assigned packages
                      <SvgIcon name={pkgMenuOpen ? "chevron-up" : "chevron-down"} size={11} color="#145a32" />
                    </button>
                    {pkgMenuOpen && (
                      <>
                        {/* Click-away catcher behind the panel. */}
                        <span
                          onClick={() => setPkgMenuOpen(false)}
                          style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, zIndex: 20 }}
                        />
                        {/* Spans, not divs — this sits inside a <p>. */}
                        <span style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 21, display: "block", minWidth: "220px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: "8px", boxShadow: "0 6px 18px rgba(13,35,63,0.14)", padding: "4px", textAlign: "left" }}>
                          {packages.map((pkg) => (
                            <button
                              key={pkg.id}
                              onClick={() => openPackageDashboard(pkg)}
                              title="View package dashboard"
                              style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%", padding: "7px 9px", background: "none", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "600", color: C.text, textAlign: "left", whiteSpace: "nowrap" }}
                            >
                              <SvgIcon name="package" size={12} color="#145a32" />
                              {pkg.packageCode} · {pkg.status}
                            </button>
                          ))}
                        </span>
                      </>
                    )}
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              {billingSetup?.billingAccountId && !packageLoading && (
                <IpdButton
                  variant="outlined"
                  onClick={() => setShowAssignModal(true)}
                  startIcon={<SvgIcon name="package" size={15} />}
                >
                  {packages.length > 0 ? "Add Package" : "Assign Package"}
                </IpdButton>
              )}
              {/*
                * Bulk Import
                * API: POST ${REACT_APP_BACKEND_ROUTE_URL}bulk_import_ipd_charges/
                * Body: FormData with file + encounter_id
                */}
              <IpdButton variant="outlined" startIcon={<SvgIcon name="upload" size={15} />}>
                Bulk import
              </IpdButton>
              {/* An emergency visit's charges are added in the ED, where they are recorded. */}
              {!isEmergencySubject(selectedPatient) && (
                <IpdButton
                  onClick={() => history.push("/ipd-billing/charge-capture/add", {
                    admissionId: encounterId,
                    defaultChargeDate,
                    patientName,
                  })}
                  startIcon={<SvgIcon name="plus" size={15} />}
                >
                  Add Charge
                </IpdButton>
              )}
            </div>
          </div>

          {/* Category filter pills (server-side filtering via ?category=<id>) +
              date filter toggle (server-side via ?today=<bool>) */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {categoryFilters.map(cat => {
                const active = activeCategory === cat.id;
                return (
                  <button key={cat.id} className="filter-tab"
                    onClick={() => setActiveCategory(cat.id)}
                    style={{ padding: "6px 14px", borderRadius: "100px", border: `1.5px solid ${active ? C.primary : C.border}`, background: active ? C.primary : "#fff", color: active ? "#fff" : C.textSub, fontSize: "13px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s" }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", color: C.text, cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={showAllDates}
                onChange={e => setShowAllDates(e.target.checked)}
                style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: C.primary }}
              />
              Show all charges
            </label>
          </div>

          {/* Charges table */}
          <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)", flex: 1 }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: C.text }}>{showAllDates ? "All Charges" : "Today's Charges"}</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: C.textSub, fontWeight: "500" }}>
                <SvgIcon name="sync" size={13} color={C.textSub} />
                {syncLabel}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {[
                      { label: "GROUP",   key: "serviceGroup" },
                      { label: "CLASSIFICATION",  key: "classification" },
                      { label: "SERVICE", key: "medicalService" },
                      { label: "DATE",            key: "time" },
                      { label: "QTY", align: "right" },
                      { label: "RATE", align: "right" },
                      { label: "AMOUNT", align: "right" },
                      { label: "PACKAGE", align: "right" },
                      { label: "", align: "right" },
                    ].map((col, i) => {
                      const sortable = Boolean(col.key);
                      const active   = sortable && sortConfig.key === col.key;
                      return (
                        <th key={i}
                          onClick={sortable ? () => handleSort(col.key) : undefined}
                          style={{ padding: "10px 14px", fontSize: "10px", fontWeight: "700", color: active ? C.primary : C.textSub, letterSpacing: "0.07em", textAlign: col.align === "right" ? "right" : "left", whiteSpace: "nowrap", textTransform: "uppercase", cursor: sortable ? "pointer" : "default", userSelect: "none" }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                            {col.label}
                            {sortable && (
                              <span style={{ fontSize: "9px", color: active ? C.primary : "#c0c7d0" }}>
                                {active ? (sortConfig.direction === "asc" ? "▲" : "▼") : "↕"}
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {isLoading
                    ? Array(5).fill(0).map((_, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          {Array(9).fill(0).map((_, j) => (
                            <td key={j} style={{ padding: "14px" }}>
                              <div style={{ height: "13px", ...shimmerStyle }} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : displayCharges.length > 0
                      ? displayCharges.map((row, idx) => {
                          const sgColor    = SERVICE_GROUP_COLORS[row.serviceGroup] || SERVICE_GROUP_COLORS.default;
                          const rowActions = chargeRowActions(row, isEmergencySubject(selectedPatient), edCanAdjust);
                          return (
                            <tr key={row.id} className="charge-row"
                              style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? "#fff" : "#fafbfc", transition: "background 0.15s" }}
                            >
                              <td style={{ padding: "13px 14px", whiteSpace: "nowrap" }}>
                                <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 9px", borderRadius: "100px", background: sgColor.bg, color: sgColor.color, whiteSpace: "nowrap" }}>{row.serviceGroup || "—"}</span>
                              </td>
                              <td style={{ padding: "13px 14px", fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{row.classification || "—"}</td>
                              <td style={{ padding: "13px 14px", fontSize: "13px", color: C.text, maxWidth: "200px" }}>
                                {row.medicalService || "—"}
                                {row._aggregated && <span style={{ marginLeft: "6px", fontSize: "11px", fontWeight: "700", color: C.textSub }}>· {row._bedDays} days</span>}
                              </td>
                              <td style={{ padding: "13px 14px", fontSize: "12px", fontWeight: "600", color: C.textSub, whiteSpace: "nowrap" }}>{row.time}</td>
                              <td style={{ padding: "13px 14px", fontSize: "13px", fontWeight: "600", color: C.text, textAlign: "right" }}>{row.qty}</td>
                              <td style={{ padding: "13px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                                {row.pricingApplied && row.standardUnitPrice != null && row.standardUnitPrice > row.rate ? (
                                  <>
                                    <div style={{ fontSize: "11px", color: C.textSub, textDecoration: "line-through" }}>{fmtINR(row.standardUnitPrice)}</div>
                                    <div style={{ fontSize: "13px", fontWeight: "600", color: C.text }}>{fmtINR(row.rate)}</div>
                                  </>
                                ) : (
                                  <span style={{ fontSize: "13px", color: C.textSub }}>{fmtINR(row.rate)}</span>
                                )}
                              </td>
                              <td style={{ padding: "13px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                                <div style={{ fontSize: "13px", fontWeight: "700", color: rowActions.spent ? C.textSub : C.text, textDecoration: rowActions.spent ? "line-through" : "none" }}>{fmtINR(row.amount)}</div>
                                {rowActions.spent && (
                                  <span style={{ padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: "700", background: "#FDECEA", color: C.danger }}>REVERSED</span>
                                )}
                                {row.pricingApplied && row.corporateDiscountAmount > 0 && (
                                  <div style={{ fontSize: "10px", fontWeight: "700", color: C.success }}>saved {fmtINR(row.corporateDiscountAmount)}</div>
                                )}
                              </td>
                              <td style={{ padding: "13px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                                {(() => {
                                  const badge = packagePricingBadge(row.packagePricingStatus);
                                  if (!badge) return <span style={{ fontSize: "12px", color: C.textSub }}>—</span>;
                                  return (
                                    <span style={{ padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap", background: badge.bg, color: badge.color }}>{badge.label}</span>
                                  );
                                })()}
                              </td>
                              <td style={{ padding: "13px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                                {(row.packagePricingStatus === "OUT_OF_PACKAGE" || row.packagePricingStatus === "PARTIALLY_INCLUDED") && packages.length > 0 && !row.isPackageCharge && (
                                  <button onClick={() => setIncludeRow(row)}
                                    title="Include this service in the patient's package"
                                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ccc", padding: "4px", borderRadius: "4px", display: "inline-flex", alignItems: "center", transition: "color 0.15s", marginRight: "2px" }}
                                    onMouseEnter={e => e.currentTarget.style.color = C.success}
                                    onMouseLeave={e => e.currentTarget.style.color = "#ccc"}
                                  >
                                    <SvgIcon name="plus" size={15} color="currentColor" />
                                  </button>
                                )}
                                {/* Edit/delete act on one standing charge: not on an aggregated bed
                                    line, not on a reversed charge, and no edit for an emergency one. */}
                                {rowActions.canEdit && (
                                  <button onClick={() => setChargeToEdit(row)}
                                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ccc", padding: "4px", borderRadius: "4px", display: "inline-flex", alignItems: "center", transition: "color 0.15s" }}
                                    onMouseEnter={e => e.currentTarget.style.color = C.primary}
                                    onMouseLeave={e => e.currentTarget.style.color = "#ccc"}
                                  >
                                    <SvgIcon name="edit" size={15} color="currentColor" />
                                  </button>
                                )}
                                {rowActions.canDelete && (
                                  <button onClick={() => setChargeToDelete(row)}
                                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ccc", padding: "4px", borderRadius: "4px", display: "inline-flex", alignItems: "center", transition: "color 0.15s", marginLeft: "2px" }}
                                    onMouseEnter={e => e.currentTarget.style.color = C.danger}
                                    onMouseLeave={e => e.currentTarget.style.color = "#ccc"}
                                  >
                                    <SvgIcon name="trash" size={15} color="currentColor" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      : (
                          <tr>
                            <td colSpan="9" style={{ padding: "40px", textAlign: "center", fontSize: "14px", color: loadError ? C.danger : C.textSub }}>
                              {loadError || "No charges found for selected filter."}
                            </td>
                          </tr>
                        )
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div style={{ width: "260px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "14px" }}>

          {/* Today's Total */}
          <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, padding: "20px", boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>Today's Total</div>
            <div style={{ fontSize: "32px", fontWeight: "800", color: C.text, marginBottom: "16px" }}>{fmtINR(todaysTotal)}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {[
                { label: "Auto-captured",    value: fmtINR(autoCaptured),    color: C.text    },
                { label: "Manual entries",   value: fmtINR(manualEntries),   color: C.text    },
                { label: "Pending approval", value: fmtINR(pendingApproval), color: C.warning },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "12px", color: C.textSub }}>{item.label}</span>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: item.color }}>{item.value}</span>
                </div>
              ))}
              {corporateSavings > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "8px", borderTop: `1px dashed ${C.border}` }}>
                  <span style={{ fontSize: "12px", color: C.textSub }}>Contract savings</span>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: C.success }}>{fmtINR(corporateSavings)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sync — charges are already posted on add; this re-pulls the account. */}
          {/* <button
            onClick={handlePostCharges}
            disabled={isPosting || !encounterId}
            style={{ width: "100%", padding: "14px", background: postSuccess ? C.success : C.primary, color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "700", cursor: !encounterId ? "not-allowed" : "pointer", boxShadow: "0 4px 14px rgba(13,35,63,0.25)", transition: "all 0.2s", opacity: !encounterId ? 0.6 : 1 }}
            onMouseEnter={e => { if (!isPosting && encounterId) e.currentTarget.style.background = postSuccess ? "#146c43" : C.primaryHover; }}
            onMouseLeave={e => { e.currentTarget.style.background = postSuccess ? C.success : C.primary; }}
          >
            {isPosting ? "Syncing…" : postSuccess ? "✓ Synced" : "Sync Charges"}
          </button> */}

          {postSuccess && (
            <div style={{ background: "#d1f2db", border: "1px solid #badbcc", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#145a32", fontWeight: "600", animation: "ipdFadeIn 0.3s ease", textAlign: "center" }}>
              Charges synced from account
            </div>
          )}
        </div>
        </div>
        )}
      </div>

      {showAssignModal && (
        <PackageAssignModal
          admissionId={encounterId}
          payerId={billingSetup?.billingPayerId}
          payerType={billingSetup?.payerType}
          bedTypeCode={selectedPatient?.bed_type_code}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => {
            setShowAssignModal(false);
            loadPackageStatus(encounterId);
            loadCharges();
          }}
        />
      )}

      {chargeToDelete && (
        <DeleteChargeModal
          charge={chargeToDelete}
          onClose={() => setChargeToDelete(null)}
          onConfirm={(reason) => handleDeleteCharge(chargeToDelete, reason)}
        />
      )}

      {chargeToEdit && (
        <EditChargeModal
          charge={chargeToEdit}
          onClose={() => setChargeToEdit(null)}
          onConfirm={(payload) => handleUpdateCharge(chargeToEdit, payload)}
        />
      )}

      {includeRow && (
        <IncludeInPackageModal
          charge={includeRow}
          packages={packages}
          onClose={() => setIncludeRow(null)}
          onConfirm={(opts) => handleIncludeInPackage(includeRow, opts)}
        />
      )}
    </IPDBillingLayout>
  );
}

// Include an out-of-package charge into the patient's package for this patient only.
// Choose which package (when >1), optionally cap the coverage, and give a reason.
function IncludeInPackageModal({ charge, packages, onClose, onConfirm }) {
  const [assignmentId, setAssignmentId] = useState(packages[0]?.id || "");
  const [capped, setCapped]             = useState(false);
  const [amountLimit, setAmountLimit]   = useState("");
  const [reason, setReason]             = useState("");
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState("");

  const submit = async () => {
    if (!assignmentId) { setError("Select a package"); return; }
    if (!reason.trim()) { setError("Reason is required"); return; }
    setBusy(true); setError("");
    try {
      await onConfirm({
        assignmentId,
        limitType: capped ? "AMOUNT_LIMIT" : "NO_LIMIT",
        amountLimit: capped ? amountLimit : undefined,
        reason: reason.trim(),
      });
    } catch (err) {
      setError(String(err?.message || "Could not include service"));
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "440px", maxWidth: "92vw", boxShadow: "0 12px 40px rgba(13,35,63,0.3)" }}>
        <div style={{ fontSize: "17px", fontWeight: "800", color: C.text, marginBottom: "6px" }}>Include in package</div>
        <div style={{ fontSize: "13px", color: C.textSub, marginBottom: "16px" }}>
          Covers <b style={{ color: C.text }}>{charge.medicalService || charge.code}</b> for this patient only. The package master and other patients are unaffected.
        </div>

        <label style={{ fontSize: "12px", fontWeight: "700", color: C.textSub }}>Package</label>
        <select value={assignmentId} onChange={(e) => setAssignmentId(e.target.value)}
          style={{ width: "100%", padding: "9px 10px", margin: "6px 0 14px", border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px" }}>
          {packages.map((p) => (
            <option key={p.id} value={p.id}>{p.packageCode}{p.packageName ? ` · ${p.packageName}` : ""}</option>
          ))}
        </select>

        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.text, marginBottom: capped ? "8px" : "14px" }}>
          <input type="checkbox" checked={capped} onChange={(e) => setCapped(e.target.checked)} />
          Cap coverage (otherwise fully covered)
        </label>
        {capped && (
          <input type="number" value={amountLimit} onChange={(e) => setAmountLimit(e.target.value)}
            placeholder="Covered amount limit (₹)"
            style={{ width: "100%", padding: "9px 10px", marginBottom: "14px", border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px" }} />
        )}

        <label style={{ fontSize: "12px", fontWeight: "700", color: C.textSub }}>Reason <span style={{ color: C.danger }}>*</span></label>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. goodwill / clinician request"
          style={{ width: "100%", padding: "9px 10px", margin: "6px 0 8px", border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px" }} />

        {error && <div style={{ fontSize: "12px", color: C.danger, marginBottom: "10px" }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding: "9px 16px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: C.textSub, cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} disabled={busy || !reason.trim()}
            style={{ padding: "9px 16px", background: C.success, border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#fff", cursor: (busy || !reason.trim()) ? "not-allowed" : "pointer", opacity: (busy || !reason.trim()) ? 0.6 : 1 }}>
            {busy ? "Including…" : "Include in package"}
          </button>
        </div>
      </div>
    </div>
  );
}
